import React, { useCallback, useEffect, useState, useRef } from 'react';
import { PasswordField } from 'Components/HookFormFields/PasswordField';
import { InputField } from 'Components/HookFormFields/InputField';
import { EmailField } from 'Components/HookFormFields/EmailField';
import { BoxInputField } from 'Components/HookFormFields/BoxInputField';
import { SelectField } from 'Components/HookFormFields/SelectField';
import './Form.css';
import './FormField.css';

const AUTO_VALIDATE_DEBOUNCE_TIME = 200;
const HANDLE_CHANGE_DEBOUNCE_TIME = 200;

/*
** For use with useForm hook **

note: async validators are separate asyncValidate array. haven't tested/used them yet.

Future Considerations:
-May consider making forms have state colocation - state in each field
(https://epicreact.dev/improve-the-performance-of-your-react-forms/)
but for now there would be issues in doing that and would be a premature optimization.
-Consider React.memo on FormField in future if see performance issues. For now would be premature optimization.
*/

const fieldComponents = {
  PasswordField,
  InputField,
  EmailField,
  BoxInputField,
  SelectField,
};

export const FormField = ({
  name,
  component,
  className,
  isDisabled,
  validate = [],
  validateDebounceTime = HANDLE_CHANGE_DEBOUNCE_TIME,
  asyncValidate = [],
  validateOnBlur = [],
  asyncValidateOnBlur = [],
  normalize, //func
  fieldErrors,
  fieldValues,
  updateValue,
  updateValidating,
  updateError,
  formSubmitted,
  //props that aren't used in this component - are simply passed to field component:
  label,
  autofocus,
  autoComplete,
  placeholder,
  warn,
  options,
  onChange, // an optional function external to form for logic when value changes
  optionalSuccessMark,
  type,
  autoValidate = false,
  formName,
}) => {
  const [touched, setTouched] = useState(false);

  const value = fieldValues[name];
  const error = fieldErrors[name];

  // runs all available validation functions for this field
  const runValidate = useCallback(
    async (newFormValues = null, isOnBlur = false) => {
      const formValues = newFormValues ? newFormValues : fieldValues;
      const thisFieldValue = formValues[name];
      if (isOnBlur) {
        if (validateOnBlur.length) {
          updateValidating(name, true);
          for (let i = 0; i < validateOnBlur.length; i++) {
            const error = validateOnBlur[i](thisFieldValue, formValues);
            if (error) {
              updateError(name, error);
              updateValidating(name, false);
              return;
            }
          }
        }
      } else if (validate.length) {
        updateValidating(name, true);
        for (let i = 0; i < validate.length; i++) {
          const error = validate[i](thisFieldValue, formValues);
          if (error) {
            updateError(name, error);
            updateValidating(name, false);
            return;
          }
        }
      }
      if (isOnBlur) {
        if (asyncValidateOnBlur.length) {
          updateValidating(name, true);
          for (let i = 0; i < asyncValidateOnBlur.length; i++) {
            try {
              const result = await asyncValidateOnBlur[i](name, thisFieldValue, formValues);
            } catch (e) {
              if (e[name]) {
                const errorMsg = e[name];
                updateError(name, errorMsg);
                updateValidating(name, false);
                return;
              }
            }
          }
        }
      } else if (asyncValidate.length) {
        updateValidating(name, true);
        for (let i = 0; i < asyncValidate.length; i++) {
          try {
            const result = await asyncValidate[i](name, thisFieldValue, formValues);
          } catch (e) {
            if (e[name]) {
              const errorMsg = e[name];
              updateError(name, errorMsg);
              updateValidating(name, false);
              return;
            }
          }
        }
      }
      updateValidating(name, false);
    },
    [asyncValidate, asyncValidateOnBlur, name, updateError, updateValidating, validate, validateOnBlur, fieldValues]
  );

  //for handling initial values on mount
  useEffect(() => {
    const initialValue = value;
    const valueEventObj = { target: { value: initialValue } };
    handleChange(valueEventObj);
    if (initialValue) {
      handleBlur();
    }
    //disabling eslint rule since only want this to run once on mount
    //don't want value as a dep here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // for auto-validation in response to other form fields updating, if `autoValidate` true
  // use debouncer and serialized string to compare and check if form values have actually been updated
  //    in order to limit the number of instances `runValidate()` is called
  const serializedFormValues = useRef('');
  useEffect(() => {
    if (autoValidate) {
      const debouncer = setTimeout(() => {
        const currentValues = JSON.stringify(fieldValues);
        if (currentValues !== serializedFormValues.current) {
          serializedFormValues.current = currentValues;
          updateError(name, null); // clear error first since a different form field value has changed
          runValidate();
        }
      }, AUTO_VALIDATE_DEBOUNCE_TIME);

      return () => {
        clearTimeout(debouncer);
      };
    }
  }, [autoValidate, runValidate, fieldValues, updateError, name]);

  // on change value, update value right away but debounce the validation here as well
  const handleChangeDebouncer = useRef();
  const handleChange = useCallback(
    async (event) => {
      const newFieldValue = normalize ? normalize(event.target.value) : event.target.value;
      const newFormValues = {
        ...fieldValues,
        [name]: newFieldValue,
      };
      onChange && onChange(newFieldValue);
      updateValue(name, newFieldValue);
      if (handleChangeDebouncer.current) {
        clearTimeout(handleChangeDebouncer.current);
      }
      handleChangeDebouncer.current = setTimeout(() => {
        const currentValues = JSON.stringify(newFormValues);
        serializedFormValues.current = currentValues;
        updateError(name, null); // clear error first since this form field value has changed
        runValidate(newFormValues);
      }, validateDebounceTime);
    },
    [runValidate, updateError, name, normalize, onChange, updateValue, fieldValues, validateDebounceTime]
  );

  const handleBlur = useCallback(async () => {
    setTouched(true);
    if (!error) {
      runValidate(null, true);
    }
  }, [error, runValidate]);

  const FieldComponent = fieldComponents[component];

  return (
    <FieldComponent
      className={`${className} ${name}Field`}
      name={name}
      label={label}
      autoComplete={autoComplete}
      autofocus={autofocus}
      warn={warn}
      handleChange={handleChange}
      value={value}
      error={error}
      onBlur={handleBlur}
      touched={touched || formSubmitted}
      placeholder={placeholder}
      isDisabled={isDisabled}
      options={options}
      optionalSuccessMark
      type={type}
      formName={formName}
    />
  );
};
